import * as React from 'react';

import { $unreadMessagesCount } from '@rematter/modules/chat/_state/conversationList.state';
import { Button, cn, Col, Menu, Row } from '@rematter/ui-web';
import { useAtom, useAtomValue } from 'jotai';
import {
  LucideBuilding2,
  LucideChevronDown,
  LucideMessageSquare,
  LucideMoon,
  LucideSparkle,
  LucideSun,
  LucideUser,
  LucideX,
} from 'lucide-react';

import { NotificationBadge } from './NotificationBadge';

import {
  useAssistantSidebar,
  useChatSidebar,
  useRightSidebar,
} from '~/app/contexts/RightSidebarContext';
import { useTranslations } from '~/app/i18n/useTranslations';
import { NotificationCenter } from '~/modules/notifications/_components/NotificationCenter';
import { PermissionGate } from '~/shared/components/PermissionGate';
import { facilitiesQueryAtom } from '~/shared/components/select-fields/SelectFacility';
import { useIdentity } from '~/shared/hooks';
import { useAutoSelectFacility } from '~/shared/hooks/useUserFacilities';
import { $theme, useSystemColorScheme } from '~/shared/state/preferences.state';

export const ThemeSwitcher = () => {
  const [theme, setTheme] = useAtom($theme);
  const systemColorScheme = useSystemColorScheme();

  const effectiveColorScheme = theme.colorScheme ?? systemColorScheme;

  const [internalTheme, setInternalTheme] =
    React.useState(effectiveColorScheme);

  React.useEffect(() => {
    setInternalTheme(effectiveColorScheme);
  }, [effectiveColorScheme]);

  return (
    <Row
      className={cn(
        '@ThemeSwitcher',
        'relative h-8 w-15 rounded-sm border border-white bg-white p-0.5 text-neutral',
      )}
      onClick={() => {
        const newTheme = effectiveColorScheme === 'light' ? 'dark' : 'light';
        setInternalTheme(newTheme);
        setTimeout(() => {
          setTheme({
            colorScheme: newTheme,
          });
        }, 250);
      }}
    >
      <div
        className={cn(
          'absolute left-px size-7 rounded-xs bg-neutral/80 transition-all',
          internalTheme === 'dark' && 'left-[29px]',
        )}
      />
      <Row xCenter className={'size-6'}>
        <LucideSun
          className={cn(
            'z-10 ml-1 size-4 text-neutral opacity-40 transition-all',
            internalTheme === 'light' && 'text-white opacity-100',
          )}
        />
      </Row>
      <Row xCenter className={'size-6'}>
        <LucideMoon
          className={cn(
            'z-10 ml-2 size-4 text-neutral opacity-40 transition-all',
            internalTheme === 'dark' && 'text-white opacity-100',
          )}
        />
      </Row>
    </Row>
  );
};

const $facilities = facilitiesQueryAtom();

const FacilitySelect = () => {
  const t = useTranslations();
  const { facilityId, setFacilityId } = useIdentity();
  const { data: facilities, isLoading } = useAtomValue($facilities);
  useAutoSelectFacility({ facilities: facilities ?? [], isLoading });

  const selectedFacility = React.useMemo(
    () => (facilities ?? []).find((f) => f.id === facilityId),
    [facilities, facilityId],
  );

  const menuItems = React.useMemo(
    () =>
      (facilities ?? []).map((f) => ({
        label: f.facilityName,
        onAction: () => setFacilityId(f.id),
      })),
    [facilities, setFacilityId],
  );

  return (
    <Menu
      trigger={
        <Button
          variant="ghost"
          size="small"
          color="white"
          className="h-8 gap-2 rounded border border-white/30 bg-white/10 px-3 text-sm text-white hover:bg-white/20"
        >
          <LucideBuilding2 size={16} />
          <span>{selectedFacility?.facilityName ?? t.topbar.facility}</span>
          <LucideChevronDown size={14} className="opacity-60" />
        </Button>
      }
      items={menuItems}
      placement="bottom left"
    />
  );
};

export const Topbar = () => {
  const t = useTranslations();
  const { user, signOut } = useIdentity();
  const { sidebarType } = useRightSidebar();
  const { toggleChat } = useChatSidebar();
  const { isAssistantOpen, toggleAssistant } = useAssistantSidebar();

  const isChatOpen = sidebarType === 'chat';

  const unreadCount = useAtomValue($unreadMessagesCount);

  return (
    <Col className="@Topbar contents">
      <header className="relative z-50 flex h-14 items-center justify-between gap-6 bg-neutral px-6 py-3 shadow">
        <h1 className="grow text-xl font-semibold text-white">&nbsp;</h1>
        <Row yCenter gap2>
          <FacilitySelect />

          <PermissionGate action="notificationRead">
            <NotificationCenter />
          </PermissionGate>

          <Row yCenter gap2 className="relative">
            <Button
              onClick={toggleChat}
              variant="solid"
              size="icon"
              color="primary"
              className="border border-white/30 bg-white/10 text-white"
              icon={isChatOpen ? <LucideX /> : <LucideMessageSquare />}
            />

            {!isChatOpen && <NotificationBadge count={unreadCount} />}
          </Row>

          <Button
            onClick={toggleAssistant}
            variant="solid"
            size="icon"
            color="primary"
            className="border border-white/30 bg-white/10 text-white"
            icon={isAssistantOpen ? <LucideX /> : <LucideSparkle />}
          />

          <div className="flex h-8 items-center gap-2 rounded border border-white/30 bg-white/10 px-3 text-sm text-white">
            <LucideUser size={16} />
            <span>
              {user?.firstName} {user?.lastName}
            </span>
          </div>
        </Row>

        <Row yCenter className="gap-6">
          <ThemeSwitcher />

          <button
            onClick={() => signOut()}
            className="text-sm text-white transition-colors hover:text-white/70"
          >
            {t.topbar.signOut}
          </button>
        </Row>
      </header>
    </Col>
  );
};
